import { useState } from 'react';
import {
  useCreateKfoGuaranteeLetterMutation,
  useGetKfoGuaranteesQuery,
  useLazyExportKfoGuaranteeLetterDocxQuery,
  type KfoLine,
  type KfoRfiSource,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import {
  Actions,
  Banner,
  DataTable,
  Dialog,
  Field,
  Filters,
  Muted,
  Pager,
  QtyInput,
  Row,
  SOURCE_LABEL,
  SmallInput,
  SmallSelect,
  Stat,
  Stats,
  TableWrap,
  Tag,
  errorText,
  fmtDate,
  fmtNum,
  guaranteeClosedText,
  isGuaranteeOverdue,
  isNotFound,
  parseQty,
  qtyToInput,
  safeFileName,
  saveBlob,
  todayIso,
} from './ui';

/** Скачивание DOCX гарантийного письма (этап 3); 404 — сервер ещё не умеет. */
function useLetterDownload(projectId: string) {
  const [trigger, { isFetching }] = useLazyExportKfoGuaranteeLetterDocxQuery();
  const [err, setErr] = useState<string | null>(null);
  async function download(letterNo: string) {
    setErr(null);
    try {
      const blob = await trigger({ projectId, letterNo }).unwrap();
      saveBlob(blob, `${safeFileName(`Гарантийное_письмо_${letterNo}`)}.docx`);
    } catch (e) {
      setErr(
        isNotFound(e)
          ? 'Выгрузка гарантийного письма в DOCX на сервере пока не включена.'
          : errorText(e, 'Не удалось сформировать DOCX письма.'),
      );
    }
  }
  return { download, downloading: isFetching, downloadError: err };
}

/**
 * Реестр гарантийных обязательств по ИД: из среза Заказчика и наши письма.
 * Просроченные подсвечены; остаток и отставание сервер считает при чтении.
 * Закрытое ГО (§12: учтено Заказчиком или ИД приняты) показывает причину и
 * дату закрытия и просроченным не подсвечивается.
 */
export function KfoGuaranteesTab({
  projectId,
  canWrite,
  selection,
  onClearSelection,
  onGoRegistry,
  letterOpen,
  onLetterOpen,
  onOpenLine,
}: {
  projectId: string;
  canWrite: boolean;
  selection: Map<string, KfoLine>;
  onClearSelection: () => void;
  onGoRegistry: () => void;
  letterOpen: boolean;
  onLetterOpen: (open: boolean) => void;
  onOpenLine: (lineId: string) => void;
}) {
  const [overdue, setOverdue] = useState(false);
  const [source, setSource] = useState<KfoRfiSource | ''>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const dq = useDebouncedValue(q.trim(), 400);

  const { data, isFetching, error } = useGetKfoGuaranteesQuery(
    {
      projectId,
      overdue: overdue || undefined,
      source,
      q: dq || undefined,
      page: page + 1,
      per_page: perPage,
    },
    { skip: !projectId },
  );
  const { download, downloading, downloadError } = useLetterDownload(projectId);

  const rows = data?.items ?? [];
  const pageQty = rows.reduce((s, g) => s + (g.qty ?? 0), 0);
  const pageRemainder = rows.reduce((s, g) => s + (g.remainder ?? 0), 0);

  return (
    <div>
      <Row>
        <Stats style={{ marginBottom: 0 }}>
          <Stat>
            <b>{(data?.total ?? 0).toLocaleString('ru-RU')}</b>
            <span>обязательств{hasAnyFilter(overdue, source, q) ? ' (по фильтру)' : ''}</span>
          </Stat>
          <Stat>
            <b style={{ color: (data?.overdue_total ?? 0) > 0 ? 'crimson' : undefined }}>
              {(data?.overdue_total ?? 0).toLocaleString('ru-RU')}
            </b>
            <span>просрочено</span>
          </Stat>
        </Stats>
        {canWrite && (
          <Button type="button" onClick={() => onLetterOpen(true)}>
            Гарантийное письмо{selection.size > 0 ? ` (${selection.size})` : ''}…
          </Button>
        )}
      </Row>

      <Filters>
        <Field>
          Источник
          <SmallSelect
            value={source}
            onChange={(e) => {
              setSource(e.target.value as KfoRfiSource | '');
              setPage(0);
            }}
          >
            <option value="">все</option>
            <option value="customer">{SOURCE_LABEL.customer}</option>
            <option value="osnova">{SOURCE_LABEL.osnova}</option>
          </SmallSelect>
        </Field>
        <Field style={{ flex: '1 1 220px' }}>
          № письма или Ключ
          <SmallInput
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
          />
        </Field>
        <Field style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 34 }}>
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked);
              setPage(0);
            }}
          />
          только просроченные
        </Field>
      </Filters>

      {error && !isNotFound(error) ? (
        <Banner $tone="danger">
          {errorText(error, 'Не удалось загрузить гарантийные обязательства.')}
        </Banner>
      ) : null}
      {downloadError && <Banner $tone="danger">{downloadError}</Banner>}

      <TableWrap style={{ opacity: isFetching ? 0.6 : 1 }}>
        <DataTable>
          <thead>
            <tr>
              <th>Письмо</th>
              <th>Срок</th>
              <th>Ключ</th>
              <th>Лист РД</th>
              <th>Шифр и наименование ЕР</th>
              <th>Элемент</th>
              <th>Ед.</th>
              <th className="num">Объём ГО</th>
              <th className="num">ИД предъяв.</th>
              <th className="num">ИД принято</th>
              <th className="num">Остаток</th>
              <th
                className="num"
                title="Срок ГО минус дата отчёта (открытое) или дата принятия ИД (закрытое)"
              >
                Отстав., дн.
              </th>
              <th>Источник</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={14} style={{ textAlign: 'center', padding: 28 }}>
                  <Muted>{isFetching ? 'Загрузка…' : 'Гарантийных обязательств нет.'}</Muted>
                </td>
              </tr>
            )}
            {rows.map((g) => {
              const overdueOpen = isGuaranteeOverdue(g);
              const closed = guaranteeClosedText(g);
              return (
                <tr
                  key={g.id}
                  data-tone={overdueOpen ? 'danger' : closed ? 'muted' : undefined}
                  data-click={g.line_id ? 'true' : undefined}
                  onClick={() => g.line_id && onOpenLine(g.line_id)}
                >
                  <td className="nowrap">
                    {g.letter_no}
                    {g.letter_date ? (
                      <Muted style={{ fontSize: 11 }}>от {fmtDate(g.letter_date)}</Muted>
                    ) : null}
                  </td>
                  <td className="nowrap">
                    {fmtDate(g.due_date)}
                    {overdueOpen && (
                      <div>
                        <Tag $tone="danger">просрочено</Tag>
                      </div>
                    )}
                    {closed && (
                      <div>
                        <Tag $tone="success" title="ГО закрыто">
                          {closed}
                        </Tag>
                      </div>
                    )}
                  </td>
                  <td className="nowrap">{g.ext_key ?? ''}</td>
                  <td className="nowrap">{g.rd_sheet_code}</td>
                  <td className="wrap" style={{ minWidth: 180, maxWidth: 280 }}>
                    <b>{g.ep_code}</b>
                    {g.ep_name ? <Muted style={{ fontSize: 11 }}>{g.ep_name}</Muted> : null}
                  </td>
                  <td className="wrap" style={{ minWidth: 120, maxWidth: 200 }}>
                    {g.element}
                  </td>
                  <td className="nowrap">{g.unit}</td>
                  <td className="num">{fmtNum(g.qty)}</td>
                  <td className="num">{fmtNum(g.id_presented)}</td>
                  <td className="num">
                    {fmtNum(g.id_accepted)}
                    {g.id_accepted_date ? (
                      <Muted style={{ fontSize: 11 }}>{fmtDate(g.id_accepted_date)}</Muted>
                    ) : null}
                  </td>
                  <td className="num">{fmtNum(g.remainder)}</td>
                  <td className="num">{g.lag_days ?? ''}</td>
                  <td>{SOURCE_LABEL[g.source] ?? g.source}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {g.source === 'osnova' && g.letter_no && (
                      <Button
                        $size="sm"
                        $variant="ghost"
                        type="button"
                        disabled={downloading}
                        onClick={() => download(g.letter_no!)}
                        title="Скачать гарантийное письмо (DOCX)"
                      >
                        DOCX
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={7}>Итого по странице</td>
                <td className="num">{fmtNum(pageQty)}</td>
                <td colSpan={2}></td>
                <td className="num">{fmtNum(pageRemainder)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </DataTable>
      </TableWrap>
      <Pager
        page={page}
        perPage={perPage}
        total={data?.total ?? 0}
        unit="обязательств"
        onPage={setPage}
        onPerPage={(n) => {
          setPerPage(n);
          setPage(0);
        }}
      />

      {letterOpen && (
        <GuaranteeLetterDialog
          projectId={projectId}
          lines={Array.from(selection.values())}
          onClose={() => onLetterOpen(false)}
          onGoRegistry={() => {
            onLetterOpen(false);
            onGoRegistry();
          }}
          onDone={() => {
            onLetterOpen(false);
            onClearSelection();
          }}
        />
      )}
    </div>
  );
}

function hasAnyFilter(overdue: boolean, source: string, q: string): boolean {
  return overdue || !!source || !!q.trim();
}

/** Объём ГО по умолчанию: выполнено, но ИД ещё не принята (не меньше нуля). */
function defaultQty(l: KfoLine): string {
  const v = (l.fact_cum ?? 0) - (l.id_accepted ?? 0);
  return v > 0 ? qtyToInput(Math.round(v * 1000) / 1000) : '';
}

/**
 * Гарантийное письмо (этап 3, §10): отмеченные в реестре строки, № и дата
 * письма, срок предоставления ИД. Сервер создаёт ГО (source = ОСНОВА) и срок в
 * календаре для напоминаний; после — DOCX письма.
 */
function GuaranteeLetterDialog({
  projectId,
  lines,
  onClose,
  onGoRegistry,
  onDone,
}: {
  projectId: string;
  lines: KfoLine[];
  onClose: () => void;
  onGoRegistry: () => void;
  onDone: () => void;
}) {
  const [create, { isLoading }] = useCreateKfoGuaranteeLetterMutation();
  const { download, downloading, downloadError } = useLetterDownload(projectId);
  const [letterNo, setLetterNo] = useState('');
  const [letterDate, setLetterDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((l) => [l.id, defaultQty(l)])),
  );
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ letterNo: string; count: number } | null>(null);

  const active = lines.filter((l) => !excluded.has(l.id));

  async function onSubmit() {
    setError(null);
    if (!letterNo.trim() || !letterDate || !dueDate) {
      setError('Заполните № письма, дату письма и срок предоставления ИД.');
      return;
    }
    if (dueDate < letterDate) {
      setError('Срок предоставления ИД раньше даты письма.');
      return;
    }
    const items: { line_id: string; qty: number }[] = [];
    for (const l of active) {
      const v = parseQty(qty[l.id] ?? '');
      if (v === null || v <= 0) {
        setError(`Укажите объём больше нуля по Ключу ${l.ext_key} (или уберите строку).`);
        return;
      }
      items.push({ line_id: l.id, qty: v });
    }
    if (items.length === 0) {
      setError('В письме нет строк.');
      return;
    }
    try {
      const res = await create({
        projectId,
        letter_no: letterNo.trim(),
        letter_date: letterDate,
        due_date: dueDate,
        items,
      }).unwrap();
      setIssued({ letterNo: letterNo.trim(), count: res.length || items.length });
    } catch (e) {
      setError(
        isNotFound(e)
          ? 'Выпуск гарантийных писем на сервере пока не включён.'
          : errorText(e, 'Не удалось выпустить письмо.'),
      );
    }
  }

  if (issued) {
    return (
      <Dialog open onClose={onDone} title="Гарантийное письмо создано" width={560}>
        <Banner $tone="success">
          Письмо № {issued.letterNo}: обязательств — {issued.count}. Срок {fmtDate(dueDate)}{' '}
          поставлен в календарь для напоминаний.
        </Banner>
        {downloadError && <Banner $tone="danger">{downloadError}</Banner>}
        <Actions style={{ justifyContent: 'flex-end' }}>
          <Button
            $variant="secondary"
            type="button"
            disabled={downloading}
            onClick={() => download(issued.letterNo)}
          >
            {downloading ? 'Готовлю…' : 'Скачать DOCX'}
          </Button>
          <Button type="button" onClick={onDone}>
            Готово
          </Button>
        </Actions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title="Гарантийное письмо по ИД" width={1000}>
      {lines.length === 0 ? (
        <>
          <Banner $tone="info">
            Строки для письма отмечаются галочками во вкладке «Реестр» (можно на разных страницах и
            с разными фильтрами), затем снова нажмите «Гарантийное письмо».
          </Banner>
          <Actions style={{ justifyContent: 'flex-end' }}>
            <Button $variant="secondary" type="button" onClick={onClose}>
              Закрыть
            </Button>
            <Button type="button" onClick={onGoRegistry}>
              Перейти в реестр
            </Button>
          </Actions>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field style={{ flex: '1 1 180px' }}>
              № письма
              <Input value={letterNo} onChange={(e) => setLetterNo(e.target.value)} />
            </Field>
            <Field style={{ flex: '1 1 160px' }}>
              Дата письма
              <Input
                type="date"
                value={letterDate}
                onChange={(e) => setLetterDate(e.target.value)}
              />
            </Field>
            <Field style={{ flex: '1 1 160px' }}>
              Срок предоставления ИД
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </div>
          <Muted style={{ marginBottom: 8 }}>
            Объём по умолчанию — выполнено (Факт СМР накопительно) минус принятая ИД. Поправьте при
            необходимости.
          </Muted>
          <TableWrap $maxHeight="46vh">
            <DataTable>
              <thead>
                <tr>
                  <th>Ключ</th>
                  <th>Лист РД</th>
                  <th>Шифр ЕР</th>
                  <th>Элемент</th>
                  <th>Ед.</th>
                  <th className="num">Факт накоп.</th>
                  <th className="num">ИД принято</th>
                  <th className="num">Объём в письме</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const off = excluded.has(l.id);
                  const bad = !off && (parseQty(qty[l.id] ?? '') ?? 0) <= 0;
                  return (
                    <tr key={l.id} data-tone={off ? 'muted' : undefined}>
                      <td className="nowrap">{l.ext_key}</td>
                      <td className="nowrap">{l.rd_sheet_code}</td>
                      <td className="nowrap">{l.ep_code}</td>
                      <td className="wrap" style={{ minWidth: 140 }}>
                        {l.element}
                      </td>
                      <td className="nowrap">{l.unit}</td>
                      <td className="num">{fmtNum(l.fact_cum)}</td>
                      <td className="num">{fmtNum(l.id_accepted)}</td>
                      <td className="num">
                        <QtyInput
                          value={qty[l.id] ?? ''}
                          disabled={off}
                          inputMode="decimal"
                          $invalid={bad}
                          onChange={(e) => setQty((p) => ({ ...p, [l.id]: e.target.value }))}
                          aria-label={`Объём по Ключу ${l.ext_key}`}
                        />
                      </td>
                      <td>
                        <Button
                          $size="sm"
                          $variant="ghost"
                          type="button"
                          onClick={() =>
                            setExcluded((p) => {
                              const n = new Set(p);
                              if (n.has(l.id)) n.delete(l.id);
                              else n.add(l.id);
                              return n;
                            })
                          }
                        >
                          {off ? 'Вернуть' : 'Убрать'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
          </TableWrap>
          {error && (
            <Banner $tone="danger" style={{ marginTop: 12 }}>
              {error}
            </Banner>
          )}
          <Actions style={{ justifyContent: 'flex-end', marginTop: 12 }}>
            <Button $variant="secondary" type="button" onClick={onClose}>
              Отмена
            </Button>
            <Button type="button" disabled={isLoading || active.length === 0} onClick={onSubmit}>
              {isLoading ? 'Выпускаю…' : `Выпустить письмо (${active.length})`}
            </Button>
          </Actions>
        </>
      )}
    </Dialog>
  );
}
